import React, { memo } from "react";

import { formatAudioDuration } from "../../helper";
import styles from "./summary.module.scss";

// The render pipeline's own QA gate is PENSPACE_MAX_WER = 0.15: a chunk whose
// Whisper transcript diverges from the script by more than this is retried and
// ultimately failed. Mirroring the number here means a reviewer sees the same
// line the renderer drew, rather than a threshold invented for the UI.
const WER_GATE = 0.15;
const WER_WATCH = 0.1;

/**
 * What produced this audio, shown under the player.
 *
 * Renders nothing for hand-uploaded files: they carry no `renderId`, and an
 * "unknown / n-a" row for every legacy upload would be noise. The absence of
 * this block IS the signal that a human uploaded the file.
 */
const RenderProvenance = ({ audio }) => {
  if (!audio?.renderId) return null;

  const wer = typeof audio.maxWer === "number" ? audio.maxWer : null;
  const werClass =
    wer === null ? "" : wer >= WER_GATE ? styles.werBad : wer >= WER_WATCH ? styles.werWatch : styles.werOk;

  // "attached", not "rendered": the render catalog carries no timestamp, so
  // this is when the audio was ingested. Labelling it as the render date would
  // mislead exactly when someone is trying to correlate a bad-sounding batch
  // with a voice or config change.
  const attached = audio.ingestedAt ? new Date(audio.ingestedAt) : null;
  const attachedLabel =
    attached && !Number.isNaN(attached.getTime())
      ? attached.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
      : null;

  return (
    <div className={styles.provenance}>
      <span className={styles.provenanceTag}>AI-narrated</span>
      {audio.lengthInSeconds ? <span>{formatAudioDuration(audio.lengthInSeconds)}</span> : null}
      {attachedLabel ? <span>attached {attachedLabel}</span> : null}
      {wer !== null && (
        <span className={werClass}>
          QA {(wer * 100).toFixed(1)}%
          {wer >= WER_WATCH ? " — worth a listen" : ""}
        </span>
      )}
      {audio.timingUrl || audio.timingKey ? <span>timing map ✓</span> : null}
    </div>
  );
};

export default memo(RenderProvenance);
